<template>
  <span
    class="view-reference d-inline-flex flex-wrap align-items-center gap-1"
    :id="field.key"
    :data-focus-id="getReferenceFocusId(field)"
  >
    <!-- Valor existente: clic = misma pestaña; icono = nueva pestaña (mismo patrón que FieldReference / viewField text) -->
    <template v-if="hasValue">
      <span class="d-inline-flex align-items-center gap-1 flex-wrap">
        <span
          v-if="getReferenceId(displayValue)"
          class="cursor-pointer text-decoration-underline"
          :class="referenceNameClass"
          :title="referenceLinkTitle"
          @click.stop="goToReference(field, { _id: getReferenceId(displayValue) })"
        >
          {{ referenceLabel }}{{ expenseInvoiceSuffix }}
        </span>
        <span
          v-else
          :class="referenceNameClass"
          :title="referenceLinkTitle"
        >{{ referenceLabel }}{{ expenseInvoiceSuffix }}</span>
        <a
          v-if="getReferenceId(displayValue)"
          :href="getReferenceHref(field, getReferenceId(displayValue))"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
          class="text-muted text-decoration-none"
          :title="$t('Open in new tab')"
          style="font-size: 0.8em;"
        >
          <i class="fas fa-external-link-alt"></i>
        </a>
        <MissingPrepaymentWarningIcon
          v-if="showMissingPrepaymentWarning"
          :show="true"
          :record="missingPrepaymentTooltipRecord"
          :tooltip="missingPrepaymentShippingTooltip"
        />
        <NotaComercialPendingBadge
          v-if="isContactReference"
          :contact-id="String(getReferenceId(displayValue) || '')"
        />
        <slot
          name="item-actions"
          :id="String(getReferenceId(displayValue) || '')"
          :label="referenceLabel"
        />
      </span>
    </template>
    <!-- Botón solo cuando addbutton y no hay valor (slot "agregar uno más" — una sola vez al final de la lista) -->
    <button
      v-if="shouldShowAddButton"
      type="button"
      class="btn btn-sm btn-outline-primary"
      @click="handleAddClick"
    >
      <i class="material-icons">add</i> {{ $t('addReferenceRelatedToContent', { label: $t(field.label) }) }}
    </button>
    <span v-if="!hasValue && !shouldShowAddButton" class="text-muted">-</span>
  </span>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import MissingPrepaymentWarningIcon from "@/components/gotrading/common/MissingPrepaymentWarningIcon.vue";
import NotaComercialPendingBadge from "@/modules/nota_comercial_module/components/NotaComercialPendingBadge.vue";
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";
import { fetchShippingExpenseForLabel } from "@/utils/markPaidExpenseLabel";
import { formatDateSync } from "@/utils/dateFormatter";
import { isProformSupplierExpenseType } from "@/utils/supplierExpenseTypes";

/** Pedidos de gastos por _id, compartidos entre las líneas de la misma pantalla. */
const expenseInvoiceRequests = new Map();

/**
 * Número y fecha del comprobante (`date`, no `payment_date`) de un gasto, y si es la
 * proforma del proveedor; `null` si al objeto le falta alguno de esos campos (hay que
 * pedirlo). El gasto pedido por id se toma como viene: sin `type` no es proforma.
 */
function expenseLabelInfoOf(expense, { fetched = false } = {}) {
  if (!expense || typeof expense !== "object") return null;
  if (!("invoice" in expense) && !("invoice_number" in expense)) return null;
  if (!("date" in expense)) return null;
  if (!fetched && !("type" in expense)) return null;
  return {
    invoice: String(expense.invoice || expense.invoice_number || "").trim(),
    date: expense.date || "",
    proforma: isProformSupplierExpenseType(expense.type),
  };
}
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";
import {
  buildMissingPrepaymentTooltipForShipping,
  hasMissingAssociatedPrepayment,
  isProformOrShippingModelName,
} from "@/utils/missingPrepaymentWarning";

export default {
  name: "ViewFieldReference",
  components: {
    MissingPrepaymentWarningIcon,
    NotaComercialPendingBadge,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    modelName: {
      type: String,
      default: "",
    },
    isGroupModel: {
      type: Boolean,
      default: false,
    },
    referenceOptions: {
      type: Array,
      default: () => [],
    },
    /** Si true, no mostrar el botón "add" (p. ej. en listado dynamic-model) */
    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /** INV N del gasto referenciado (`shipping_expenses.invoice`); "" = sin factura o sin cargar. */
      expenseInvoice: "",
      /** Fecha de la factura del gasto (`shipping_expenses.date`); "" = sin fecha. */
      expenseInvoiceDate: "",
      /** El gasto es la proforma del proveedor (todavía no pasó a «Invoice Supplier»). */
      expenseIsProforma: false,
      loadedReferenceObjects: {}, // Objeto reactivo para guardar objetos cargados
      loadingReferences: {}, // Objeto reactivo para evitar cargas duplicadas
      referenceLabel: "", // Variable reactiva para el label
      referenceDeleted: false, // true si la referencia está en la papelera
      referenceArchived: false, // true si la referencia está archivada
      /** paid del payment cuando se cargó por GET (objeto/ opciones sin campo paid) */
      paymentPaidFromApi: null,
      /** remaining_advance / total_remaining_advance del ref cargado por API */
      referencePendingAdvanceFromApi: null,
    };
  },
  computed: {
    isExpenseReference() {
      return String((this.field && this.field.ref) || "").toLowerCase() === "shipping_expenses";
    },
    /**
     * Ticket #495: un gasto se nombra «Expense - OPERACIÓN IMPORTE» y no dice qué
     * comprobante se paga. Se le suma su número: «PROFORMA N» mientras el gasto al
     * proveedor es la proforma, «INV N» en cualquier otro (factura, flete…).
     */
    expenseInvoiceSuffix() {
      if (!this.isExpenseReference || !this.expenseInvoice) return "";
      const date = formatDateSync(this.expenseInvoiceDate, "");
      const dateSuffix = date ? ` - ${date}` : "";
      // Solo si el nombre ya trae el número rotulado («INV N 5238/2»): suelto puede ser parte
      // de otra cosa («5238/2» está dentro de «OP5238/2026-2»).
      if (String(this.referenceLabel || "").includes(` N ${this.expenseInvoice}`)) return dateSuffix;
      const prefix = this.expenseIsProforma ? "PROFORMA N" : "INV N";
      return ` - ${prefix} ${this.expenseInvoice}${dateSuffix}`;
    },
    /** Solo los contactos llevan el aviso de notas comerciales sin aplicar. */
    isContactReference() {
      const ref = String((this.field && this.field.ref) || "").trim();
      return /^contacts?$/i.test(ref) && !!this.getReferenceId(this.displayValue);
    },
    displayValue() {
      // Si el valor es null, undefined o string vacío, retornar null
      if (this.value === null || this.value === undefined || this.value === "") {
        return null;
      }
      // Si es un objeto vacío, también retornar null
      if (typeof this.value === "object" && this.value !== null && Object.keys(this.value).length === 0) {
        return null;
      }
      return this.value;
    },
    /** ID actual para buscar en referenceOptions (string) */
    currentRefId() {
      if (!this.displayValue) return null;
      if (typeof this.displayValue === "string") return String(this.displayValue);
      if (typeof this.displayValue === "object" && this.displayValue._id != null) {
        return String(this.displayValue._id);
      }
      return null;
    },
    hasValue() {
      // Verificar si realmente hay un valor (no solo un string vacío o null)
      const val = this.displayValue;
      if (val === null || val === undefined || val === "") {
        return false;
      }
      if (typeof val === "object" && val !== null && Object.keys(val).length === 0) {
        return false;
      }
      // Si es un string que solo contiene espacios, considerarlo vacío
      if (typeof val === "string" && val.trim() === "") {
        return false;
      }
      return true;
    },
    hasAddButton() {
      return this.field.addbutton === true || this.field.addbutton === "true";
    },
    // Mostrar botón solo cuando addbutton, no hay valor y no está oculto (p. ej. en listado)
    shouldShowAddButton() {
      return this.hasAddButton && !this.hasValue && !this.hideAddButton;
    },
    isPaymentReference() {
      const r = this.field && this.field.ref;
      return r && String(r).toLowerCase() === "payment";
    },
    resolvedPaymentPaid() {
      if (!this.isPaymentReference) return null;
      const v = this.displayValue;
      if (v && typeof v === "object" && v !== null && Object.prototype.hasOwnProperty.call(v, "paid")) {
        return this.normalizePaidFlag(v.paid);
      }
      const opt = this.resolveFromReferenceOptions(this.currentRefId);
      if (opt && Object.prototype.hasOwnProperty.call(opt, "paid")) {
        return this.normalizePaidFlag(opt.paid);
      }
      if (this.paymentPaidFromApi !== null) {
        return this.paymentPaidFromApi;
      }
      return null;
    },
    referenceDisplayClass() {
      if (!this.isPaymentReference) {
        return "text-primary";
      }
      const paid = this.resolvedPaymentPaid;
      if (paid === true) {
        return "text-success";
      }
      if (paid === false) {
        return "text-danger";
      }
      return "text-primary";
    },
    referenceNameClass() {
      return [
        this.referenceDisplayClass,
        {
          "reference-in-trash": this.referenceDeleted,
          "reference-archived": this.showArchivedStyle,
        },
      ];
    },
    showArchivedStyle() {
      return this.referenceArchived && !this.referenceDeleted;
    },
    referenceLinkTitle() {
      if (this.showArchivedStyle) {
        return this.$t("This item is archived");
      }
      return this.paymentPaidStatusTitle;
    },
    /** Tooltip solo para refs payment en duntower: verde = banco; rojo = solicitud pendiente. */
    paymentPaidStatusTitle() {
      if (!this.isPaymentReference || !isDuntowerTenant()) return undefined;
      const paid = this.resolvedPaymentPaid;
      if (paid === true) {
        return this.$t("financialSummary.payments.paymentLinkPaidHint");
      }
      if (paid === false) {
        return this.$t("financialSummary.payments.paymentLinkUnpaidHint");
      }
      return undefined;
    },
    /** Solo Duntower: el aviso de anticipo pendiente es un requisito de ese tenant. */
    isProformOrShippingReference() {
      if (!isDuntowerTenant()) return false;
      return isProformOrShippingModelName(this.field && this.field.ref);
    },
    /**
     * Aviso de anticipo pendiente en cualquier columna que muestre una Proforma o un
     * embarque (listado propio, listado de otro modelo o reporte guardado): lo decide
     * el registro **referenciado** en la celda, nunca el de la fila.
     *
     * Ticket #363 — antes se miraba primero `this.item`, así que en el listado de
     * embarques la columna Proforma mostraba el pendiente del embarque de la fila, y en
     * listados de otros modelos (p. ej. Payment) no aparecía nunca.
     */
    showMissingPrepaymentWarning() {
      if (!this.hasValue || !this.isProformOrShippingReference) return false;
      // Fila de PI con el embarque de la celda poblado: decide el pendiente de ese embarque
      if (this.rowShippingHasPendingBreakdown) {
        return !!this.missingPrepaymentShippingTooltip;
      }
      const v = this.displayValue;
      if (v && typeof v === "object" && hasMissingAssociatedPrepayment(v)) {
        return true;
      }
      const fromOptions = this.resolveFromReferenceOptions(this.currentRefId);
      if (fromOptions && hasMissingAssociatedPrepayment(fromOptions)) {
        return true;
      }
      return this.referencePendingAdvanceFromApi === true;
    },
    /** Registro para armar tooltip (shipping de la celda o PI padre con shippings). */
    missingPrepaymentTooltipRecord() {
      const v = this.displayValue;
      if (v && typeof v === "object" && (v.name || v.label)) {
        return v;
      }
      const id = this.currentRefId;
      if (id && this.item && Array.isArray(this.item.shippings)) {
        const hit = this.item.shippings.find(
          (s) => s && String(s._id) === String(id)
        );
        if (hit) return hit;
      }
      const fromOptions = this.resolveFromReferenceOptions(id);
      if (fromOptions) return fromOptions;
      // Sin el referenciado a mano, mejor la leyenda base que los importes de la fila,
      // que son de otro registro (el icono ya se decidió en showMissingPrepaymentWarning).
      return null;
    },
    /** La fila trae los embarques poblados y el de esta celda está entre ellos. */
    rowShippingHasPendingBreakdown() {
      if (!this.currentRefId) return false;
      const shippings = this.item && this.item.shippings;
      if (!Array.isArray(shippings)) return false;
      return shippings.some(
        (s) => s && String(s._id) === String(this.currentRefId)
      );
    },
    /** Celda de embarque en el listado de PI: monto pendiente de ese embarque. */
    missingPrepaymentShippingTooltip() {
      return buildMissingPrepaymentTooltipForShipping(
        this.item,
        this.currentRefId,
        this.referenceLabel
      );
    },
  },
  watch: {
    currentRefId(newId, oldId) {
      if (newId !== oldId) {
        this.paymentPaidFromApi = null;
        this.referencePendingAdvanceFromApi = null;
        this.resolveExpenseInvoice({ allowFetch: true });
      }
    },
    referenceOptions: {
      handler(opts) {
        if (!this.currentRefId || !Array.isArray(opts) || opts.length === 0) return;
        const option = opts.find(
          (opt) => opt && (String(opt._id) === this.currentRefId)
        );
        if (option) {
          this.referenceLabel = option.name || option.label || option._id || this.currentRefId;
          this.applyReferenceLifecycle(option);
          if (this.isProformOrShippingReference) {
            this.referencePendingAdvanceFromApi =
              hasMissingAssociatedPrepayment(option);
          }
        }
      },
      deep: true,
    },
  },
  created() {
    // Con solo el _id y sin opción, loadReferenceObjectForView ya trae el gasto entero.
    this.resolveExpenseInvoice({
      allowFetch: typeof this.displayValue === "object" || !!this.resolveFromReferenceOptions(this.currentRefId),
    });
    // Debug: verificar el estado del campo
    if (this.field.addbutton) {
      console.log('[ViewFieldReference] Campo con addbutton:', {
        fieldKey: this.field.key,
        fieldLabel: this.field.label,
        value: this.value,
        displayValue: this.displayValue,
        hasValue: this.hasValue,
        shouldShowAddButton: this.shouldShowAddButton,
        addbutton: this.field.addbutton
      });
    }
    
    // Si es un objeto, usar directamente name o label; si tiene _id, intentar tomar deleted de referenceOptions por si el objeto no lo trae
    if (this.displayValue && typeof this.displayValue === "object" && this.displayValue !== null) {
      this.referenceLabel = this.displayValue.name || this.displayValue.label || this.displayValue._id || "";
      const fromOptions = this.resolveFromReferenceOptions(this.currentRefId);
      this.applyReferenceLifecycle(this.displayValue, fromOptions);
      if (fromOptions && (fromOptions.name || fromOptions.label)) {
        this.referenceLabel = fromOptions.name || fromOptions.label || this.referenceLabel;
      }
      if (this.isProformOrShippingReference) {
        this.referencePendingAdvanceFromApi =
          hasMissingAssociatedPrepayment(this.displayValue) ||
          hasMissingAssociatedPrepayment(fromOptions);
      }
      return;
    }
    // Si es un ID y tenemos referenceOptions (p. ej. desde ViewContent), comprobar si la opción está en trash
    const fromOptions = this.resolveFromReferenceOptions(this.currentRefId);
    if (fromOptions) {
      this.referenceLabel = fromOptions.name || fromOptions.label || fromOptions._id || this.currentRefId;
      this.applyReferenceLifecycle(fromOptions);
      if (this.isProformOrShippingReference) {
        this.referencePendingAdvanceFromApi =
          hasMissingAssociatedPrepayment(fromOptions);
      }
      return;
    }
    if (this.displayValue) {
      this.loadReferenceObjectForView();
    }
  
  },
  methods: {
    /**
     * INV N del gasto: del objeto que vino, de las opciones o, si ninguno trae el campo,
     * del gasto pedido por id (una vez por gasto en toda la pantalla).
     */
    async resolveExpenseInvoice({ allowFetch }) {
      if (!this.isExpenseReference) return;
      const id = this.currentRefId;
      this.setExpenseLabelInfo(null);
      if (!id) return;
      const known = [this.displayValue, this.resolveFromReferenceOptions(id)]
        .map(expenseLabelInfoOf)
        .find(Boolean);
      if (known) {
        this.setExpenseLabelInfo(known);
        return;
      }
      if (!allowFetch) return;
      if (!expenseInvoiceRequests.has(id)) {
        expenseInvoiceRequests.set(
          id,
          fetchShippingExpenseForLabel(wencesApi, id).then((doc) => expenseLabelInfoOf(doc, { fetched: true }))
        );
      }
      const info = await expenseInvoiceRequests.get(id);
      if (this.currentRefId === id) this.setExpenseLabelInfo(info);
    },
    setExpenseLabelInfo(info) {
      this.expenseInvoice = (info && info.invoice) || "";
      this.expenseInvoiceDate = (info && info.date) || "";
      this.expenseIsProforma = !!(info && info.proforma);
    },
    applyReferenceLifecycle(primary, fallback) {
      const src = primary && typeof primary === "object" ? primary : null;
      const fb = fallback && typeof fallback === "object" ? fallback : null;
      if (src && src.deleted !== undefined) {
        this.referenceDeleted = Boolean(src.deleted);
      } else {
        this.referenceDeleted = Boolean(fb && fb.deleted);
      }
      if (src && src.archived !== undefined) {
        this.referenceArchived = Boolean(src.archived);
      } else {
        this.referenceArchived = Boolean(fb && fb.archived);
      }
    },
    getReferenceFocusId(field) {
      if (!field || !field.key) return null;
      return `reference-${String(field.key).toLowerCase()}`;
    },
    /** Busca en referenceOptions la opción con _id === idStr (para cuando ViewContent carga opciones después de created) */
    resolveFromReferenceOptions(idStr) {
      if (!idStr || !Array.isArray(this.referenceOptions) || this.referenceOptions.length === 0) return null;
      return this.referenceOptions.find((opt) => opt && String(opt._id) === String(idStr)) || null;
    },
    getReferenceId(refValue) {
      if (!refValue) return null;
      if (typeof refValue === "object" && refValue._id) {
        return refValue._id;
      }
      return refValue;
    },
    loadReferenceObjectForView() {
  
      
      const idStr = String(this.displayValue);
      
      
      const isUserRef = this.field.ref.toLowerCase() === 'user' || this.field.ref.toLowerCase() === 'users';
      let url;
      if (isUserRef) {
        url = `/api/users/${idStr}`;
      } else {
        url = `/api/gotrading/dynamic/${this.field.ref}/${idStr}`;
      }
      // Hacer la petición y esperar el resultado
      wencesApi.get(url)
        .then((response) => {
          const { data } = response;
          if (data) {
            // Actualizar el label cuando se carga el objeto
            // La respuesta puede venir como data directamente o como data.data
            const referenceData = data.data || data;
            this.referenceLabel = referenceData.name || referenceData.label || idStr;
            this.applyReferenceLifecycle(referenceData);
            if (this.isExpenseReference) {
              this.setExpenseLabelInfo(expenseLabelInfoOf(referenceData));
            }
            if (this.isPaymentReference && referenceData && Object.prototype.hasOwnProperty.call(referenceData, "paid")) {
              this.paymentPaidFromApi = this.normalizePaidFlag(referenceData.paid);
            }
            if (this.isProformOrShippingReference) {
              this.referencePendingAdvanceFromApi =
                hasMissingAssociatedPrepayment(referenceData);
            }
          }
        })
        .catch((error) => {
          console.error(`Error loading reference object for view (ID: ${idStr}):`, error);
          // En caso de error, mostrar el ID
          this.referenceLabel = idStr;
        });
      
    },
    getReferencePath(refModel, refId) {
      if (!refModel || !refId) return "#";
      const m = String(refModel).toLowerCase();
      const isUserRef = m === "user" || m === "users";
      if (isUserRef) {
        return `/dynamic-model/users/view/${refId}`;
      }
      const title =
        typeof this.displayValue === "object" &&
        this.displayValue &&
        String(this.displayValue._id) === String(refId)
          ? this.displayValue.name || this.displayValue.label || this.referenceLabel
          : this.referenceLabel;
      return buildDynamicModelItemViewPath(refModel, refId, title);
    },
    getReferenceHref(field, refId) {
      if (!field?.ref || !refId) return "#";
      return this.getReferencePath(field.ref, refId);
    },
    goToReference(field, reference) {
      if (!reference || !reference._id) return;
      if (!field.ref) return;
      const path = this.getReferencePath(field.ref, reference._id);
      this.$router.push(path).catch((err) => {
        if (err.name !== "NavigationDuplicated") {
          console.error("Error navigating to reference:", err);
        }
      });
    },
    normalizePaidFlag(p) {
      if (p === true || p === "true" || p === 1) {
        return true;
      }
      if (p === false || p === "false" || p === 0) {
        return false;
      }
      return null;
    },
    handleAddClick() {
      // Emitir evento para que el componente padre (ViewContent) maneje la creación.
      // Va también el item de la pantalla (el documento padre): quien lo recibe lo usa
      // para nombrar la operación a la que se aplica lo que se cree.
      this.$emit("open-reference-modal", { field: this.field, item: this.item });
    },
  },
};
</script>

<style scoped>
.view-reference {
  font-weight: 500;
}

.view-reference .reference-in-trash {
  color: #b02a37 !important;
  text-decoration: line-through !important;
  font-weight: 600;
  border: 1px solid #b02a37 !important;
}
.view-reference span.reference-in-trash:hover {
  color: #8b222a !important;
}
.view-reference .reference-archived {
  color: #888888 !important;
  font-weight: 600;
}
.view-reference span.reference-archived:hover {
  color: #6e6e6e !important;
}
</style>

